import { Controller } from "@hotwired/stimulus"
import { useDocumentListener } from 'lib/event_helpers';
import { isMobileWidth } from "lib/window_helper";

export default class extends Controller {
  static targets = ["form", "input", "uuid", "submit"]

  connect() {
    this.messageListeners();
    this.formListener();
    this.formSubmitSensibly();
    this.enableShiftEnterForNewLine();
  }

  messageListeners() {
    useDocumentListener(this, 'chat--window:loaded', (event) => {
      this.setPlaceholder(event.target.dataset.requestType);
    });
    useDocumentListener(this, 'chat--message:new', (event) => {
      this.formTarget.reset();
      this.resizeTextarea();
    });
  }

  // TODO: Doesn't handle spanish translations
  setPlaceholder(requestType) {
    const placeholders = {
      "requested_contact_info": "Share your email or ask a question",
      "requested_feedback": "What could have been better?",
    };

    this.inputTarget.placeholder = placeholders[requestType] || "Type your question here";
  }

  formListener() {
    this.onFormSubmit();
    this.onBroadcastComplete();
  }

  onFormSubmit() {
    this.formTarget.addEventListener("turbo:submit-start", (event) => {
      this.dispatchWithUUID();
      this.disableSubmitButton();
    });
  }

  onBroadcastComplete() {
    useDocumentListener(this, "chat--broadcast:complete", () => {
      if (!isMobileWidth()) {
        this.inputTarget.focus();
      }
    });
  }

  dispatchWithUUID() {
    let detail = { content: this.inputTarget.value };

    if (this.uuidTarget.value) {
      detail.uuid = this.uuidTarget.value;
    }
    this.dispatch("requested", { detail: detail });
  }

  disableSubmitButton() {
    this.submitTarget.classList.remove("enabled");
    this.submitTarget.disabled = true;
  }

  enableSubmitButton() {
    this.submitTarget.classList.add("enabled");
    this.submitTarget.disabled = false;
  }

  formSubmitSensibly() {
    this.submitOnEnter();
    this.preventSubmissionIfBlank();
  }

  submitOnEnter() {
    this.inputTarget.addEventListener("keydown", (event) => {
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        if (!this._isBlank(this.inputTarget.value)) {
          this.formTarget.requestSubmit();
        }
      }
    });
  }

  preventSubmissionIfBlank() {
    this.formTarget.addEventListener("submit", (event) => {
      if (this._isBlank(this.inputTarget.value)) {
        event.preventDefault();
      }
    });
  }

  _isBlank(str) {
    return (!str || /^\s*$/.test(str));
  }

  enableShiftEnterForNewLine() {
    this.inputTarget.addEventListener('keydown', event => {
      if (event.key === 'Enter' && event.shiftKey) {
        event.stopPropagation();
      }
    });
  }

  handleInput(event) {
    // Dispatch input event for other controllers
    this.dispatch("input", event);

    // Enable/disable the submit button based on value
    if (!this._isBlank(this.inputTarget.value)) {
      this.enableSubmitButton();
    } else {
      this.disableSubmitButton();
    }

    // Auto-size the input bar if the text wraps to multiple lines
    this.resizeTextarea();
  }

  resizeTextarea() {
    const { inputTarget } = this;
    const computedStyle = getComputedStyle(inputTarget);
    const borderTopWidth = parseInt(computedStyle.borderTopWidth, 10);
    const borderBottomWidth = parseInt(computedStyle.borderBottomWidth, 10);

    inputTarget.style.height = '0';
    const newHeight = inputTarget.scrollHeight + borderTopWidth + borderBottomWidth;
    inputTarget.style.height = `${newHeight}px`;
  }
}